import React, { useState, useRef, useEffect } from "react";
import { Button } from "components/components/atoms";
import { Container } from "components/shared";
import { useSpring, animated } from "react-spring";
import styled from "styled-components";
import tw from "tailwind.macro";
import useBreakpoint from "utils/hooks/useBreakpoint";
import breakpoints from "utils/breakpoints";
import cx from "classnames";
import useOnClickOutside from "utils/hooks/useOnClickOutside";
import { getFieldValue, RichText } from "@sitecore-jss/sitecore-jss-react";
import NumberFormat from "react-number-format";
import { useMeasure, usePrevious } from "react-use";
import ABAModal from "../ABAModal";
import { Helmet } from "react-helmet";
import { track } from "utils/tracking";
import trackingEvents from "utils/trackingEvents";
import { useSitecoreContext } from "stores/sitecoreContext";
import useNewColorSchemaOptions from "utils/hooks/useNewColorSchemaOptions";

const TitleCTAContainer = styled.div`
  ${({ showNewSchemaColor, newSchemaColorHexaCode }) =>
    showNewSchemaColor ? `background-color: ${newSchemaColorHexaCode};` : ""}
`;

const Heading = styled.p`
  @media ${breakpoints.xl} {
    font-size: 2.875rem;
  }
`;

const Content = styled(animated.div)`
  @media print {
    height: auto !important;
  }
`;

const StyledButton = styled(Button)`
  @media ${breakpoints.md} {
    ${tw`px-4 h-auto`}
  }
  @media ${breakpoints.lg} {
    ${({ wider }) => (wider ? `width: 15rem;` : `width: 12.7rem;`)}
    ${({ printButton }) => printButton && `width: 15.2rem;`}
    ${tw`px-0`}
  }
`;

const ResultsTitle = styled.p`
  margin-left: -1px;
`;

const FormatedInput = styled(NumberFormat).attrs(({ className }) => ({
  className: `w-full flex justify-center mb-4 relative ${className || ""}`,
}))`
  padding: 0 0.175rem;

  & label {
    display: none;
  }

  outline: none;
  ${tw`border font-graphie text-mattamy-blue rounded-none px-4 py-2 w-full rounded bg-white`}
  border-color: rgba(179, 195, 205, 0.9);
  line-height: 1.75rem;
  height: 3.125rem;

  & textarea {
    height: 8.125rem !important;
  }
`;

const calculateMortgage = (
  totalAmount,
  designAmount,
  downpayment,
  rate,
  termYears
) => {
  const amount = totalAmount + (designAmount || 0) - (downpayment || 0);
  const adjustedRate = rate / 100 / 12;
  const termMonths = termYears * 12;
  if (adjustedRate === 0) return amount / termMonths;
  const pvif = Math.pow(1 + adjustedRate, termMonths);
  const pmt = (adjustedRate / (pvif - 1)) * (amount * pvif);
  return pmt;
};

const getPlaceholderFields = (rendering, placeholder) => {
  try {
    const fields =
      rendering.placeholders[placeholder][0] &&
      rendering.placeholders[placeholder][0].fields;
    return fields || {};
  } catch (e) {
    console.log(e);
    return {};
  }
};

const getButtonColor = (textStyle) => {
  if (textStyle === "blue-white") {
    return "action-blue";
  }
  if (textStyle === "white-blue") {
    return "white";
  }
  if (textStyle === "blue-gray") {
    return "mattamy-blue";
  }
};

const getButtonTextColor = (textStyle, cta, isMobile) => {
  if (textStyle === "blue-white") {
    return "action-blue";
  }
  if (textStyle === "white-blue") {
    if (cta.primary || isMobile) {
      return "mattamy-blue";
    }
    return "white";
  }
  if (textStyle === "blue-gray") {
    return "mattamy-blue";
  }
};

const MortgageCalculator = ({ fields, rendering }) => {
  const {
    newSchemaColorHexaCode,
    showNewSchemaColor,
  } = useNewColorSchemaOptions();
  const title = getFieldValue(fields, "title");
  const firstCta = getFieldValue(fields, "firstCta") || {};
  const secondCta = getFieldValue(fields, "secondCta") || {};
  const acknowledgment = getFieldValue(fields, "acknowledgment");
  const textStyleField = getFieldValue(fields, "textStyle");
  const textStyle = ["white-blue", "blue-white", "blue-gray"].includes(
    textStyleField
  )
    ? textStyleField
    : "white-blue";

  const calculatorFlyout = getPlaceholderFields(rendering, "calculator-flyout");
  const heading = getFieldValue(calculatorFlyout, "heading");
  const subheading = getFieldValue(calculatorFlyout, "subheading");
  const resultsTitle = getFieldValue(calculatorFlyout, "resultsTitle");
  const resultsText = getFieldValue(calculatorFlyout, "resultsText");
  const footerText = getFieldValue(calculatorFlyout, "footerText");

  const abaModalFields = getPlaceholderFields(rendering, "aba-modal");
  const mainTitle = getFieldValue(abaModalFields, "mainTitle");
  const mainText = getFieldValue(abaModalFields, "mainText");
  const table = getFieldValue(abaModalFields, "table");
  const tableMobile = getFieldValue(abaModalFields, "tableMobile");
  const secondTitle = getFieldValue(abaModalFields, "secondTitle");
  const secondText = getFieldValue(abaModalFields, "secondText");
  const abaModalCtaText = getFieldValue(abaModalFields, "ctaText");

  const [contentVisible, toggleContent] = useState(false);
  const prevContentVisible = usePrevious(contentVisible);
  const [abaModalOpen, setAbaModalOpen] = useState(false);
  const [isCalculatedButtonTouched, setIsCalculatedButtonTouched] = useState(
    false
  );

  const [designAmountValue, setDesignAmountValue] = useState();
  const [amountValue, setAmountValue] = useState();
  const [downpaymentValue, setDownpaymentValue] = useState();
  const [interestRatevalue, setInterestRateValue] = useState();
  const [loanTermValue, setLoanTermValue] = useState();

  const [showResult, setShowResult] = useState(false);
  const [ctaClickedIndex, setCtaClickedIndex] = useState(0);

  const [ref, { height }] = useMeasure();
  const defaultHeight = 0;
  const [contentHeight, setContentHeight] = useState(defaultHeight);

  const { itemPath = "" } = useSitecoreContext() || {};
  const breakpoint = useBreakpoint(
    typeof window !== "undefined" && window.innerWidth
  );
  const isMobile = ["xs", "sm"].includes(breakpoint);
  const acknowledgmentRef = useRef();

  const expand = useSpring({
    height: contentVisible ? contentHeight : defaultHeight,
  });

  const fade = useSpring({
    opacity: contentVisible ? 1 : 0,
    delay: contentVisible ? 200 : 0,
  });

  // Click outside functionality
  const contentRef = useRef();
  useOnClickOutside(contentRef, () => {
    if (!isMobile) {
      toggleContent(false);
    }
  });

  useEffect(() => {
    if (prevContentVisible && !contentVisible) {
      window.scrollTo({
        top: contentRef.offsetTop,
        left: 0,
        behavior: "smooth",
      });
    }
  }, [contentVisible]);

  useEffect(() => {
    setContentHeight(height);
    window.addEventListener("resize", setContentHeight(height));

    return window.removeEventListener("resize", setContentHeight(height));
  }, [height]);

  const handleExpand = (ctaIndex) => {
    if (acknowledgment) {
      setCtaClickedIndex(ctaIndex);
      setAbaModalOpen(true);
      setTimeout(() => {
        const element = document.getElementById("ABAModalContainer");
        if (element) {
          element.focus();
        }
      }, 0);
      track({ eventId: trackingEvents.MattamyHF });
    } else {
      toggleContent(!contentVisible);
      track({ eventId: trackingEvents.MortgageCalculator });
    }
  };

  const handlePrint = () => {
    if (typeof window !== "undefined") window.print();
  };

  const result = calculateMortgage(
    amountValue,
    designAmountValue,
    downpaymentValue,
    interestRatevalue,
    loanTermValue
  );

  const ctas = [firstCta];

  if (acknowledgment) {
    ctas.push(secondCta);
  }

  if (typeof window !== "undefined") {
    const currentPath = window.location.pathname;
    if (currentPath !== itemPath) return "";
  }
  return (
    <>
      <Helmet>
        <style>{`
          @media print {

            body #root {
              display: block !important;
            }

            body #root > div > main > *:not(.open-print) {
              display: none;
            } 
          }
        `}</style>
      </Helmet>
      <section
        //id={uid}
        className={cx(
          "w-screen flex flex-col items-center justify-start py-4 md:py-10",
          { "open-print": contentVisible }
        )}
      >
        <Container ref={contentRef}>
          <TitleCTAContainer
            showNewSchemaColor={showNewSchemaColor}
            newSchemaColorHexaCode={newSchemaColorHexaCode}
            className={cx(
              "flex flex-col items-center justify-between rounded-lg print:text-mattamy-blue print:border-none overflow-hidden",
              {
                "bg-mattamy-blue text-white":
                  !showNewSchemaColor && textStyle === "white-blue",
                "bg-white text-mattamy-blue border-2 border-action-blue":
                  !showNewSchemaColor && textStyle === "blue-white",
                "bg-mattamy-gray-bg text-mattamy-blue":
                  !showNewSchemaColor && textStyle === "blue-gray",
                "text-black": showNewSchemaColor,
              }
            )}
          >
            <div
              className={cx(
                "flex flex-col print:flex-row md:flex-row items-center justify-between w-full",
                "pl-4 md:pl-10 pr-4 md:pr-8 pt-30-mattamy rounded-lg print:pb-0",
                {
                  "pb-30-mattamy": !isMobile || (isMobile && !contentVisible),
                }
              )}
            >
              <div className="w-full lg:w-5/12 xl:w-7/12 flex md:flex-row items-left justify-left">
                <Heading
                  className={cx(
                    "text-5xl md:text-4xl tracking-wide font-bold font-trade-gothic-20",
                    "text-center md:text-left mt-4 mb-5 md:mb-2 md:mr-8 leading-tight",
                    {
                      "text-black": showNewSchemaColor,
                    }
                  )}
                >
                  {title}
                </Heading>
              </div>
              <div
                className="w-full print:hidden lg:w-7/12 xl:w-5/12 flex flex-col md:flex-row items-center
                justify-center md:justify-end md:text-right"
              >
                <div className="print:hidden w-full ">
                  {acknowledgment &&
                    ctas.map(
                      (cta, index) =>
                        cta.text &&
                        (!isMobile || (isMobile && !contentVisible)) && (
                          <StyledButton
                            key={cta.text + index}
                            className={cx({
                              "hover:bg-mattamy-blue-dark hover:text-white hover:border-mattamy-blue-dark":
                                !showNewSchemaColor &&
                                textStyle === "white-blue" &&
                                cta.primary,
                              "hover:bg-white hover:text-mattamy-blue hover:border-white":
                                !showNewSchemaColor &&
                                textStyle === "white-blue" &&
                                !cta.primary,
                              "hover:bg-black hover:text-white hover:border-black": showNewSchemaColor,
                              "ml-4": !isMobile && index === 1,
                              "mt-4": isMobile && index === 1,
                            })}
                            color={
                              showNewSchemaColor
                                ? "black"
                                : getButtonColor(textStyle)
                            }
                            variant={
                              !showNewSchemaColor && (cta.primary || isMobile)
                                ? "filled"
                                : "outlined"
                            }
                            textColor={
                              showNewSchemaColor
                                ? "black"
                                : getButtonTextColor(textStyle, cta, isMobile)
                            }
                            fullWidthMobile
                            onClick={() => handleExpand(index)}
                            buttonDescription={
                              "Explore Mattamy Home Funding. " + cta.text
                            }
                            buttonRef={index === 0 ? acknowledgmentRef : null}
                          >
                            {cta.text}
                          </StyledButton>
                        )
                    )}
                  {!acknowledgment &&
                    (!isMobile || (isMobile && !contentVisible)) && (
                      <StyledButton
                        className={cx({
                          "hover:bg-white hover:text-mattamy-blue hover:border-white":
                            !showNewSchemaColor && textStyle === "white-blue",
                          "hover:bg-black hover:text-white hover:border-black": showNewSchemaColor,
                        })}
                        color={
                          showNewSchemaColor
                            ? "black"
                            : getButtonColor(textStyle)
                        }
                        variant={
                          !showNewSchemaColor && isMobile
                            ? "filled"
                            : "outlined"
                        }
                        textColor={
                          showNewSchemaColor
                            ? "black"
                            : getButtonTextColor(
                                textStyle,
                                { primary: false },
                                isMobile
                              )
                        }
                        fullWidthMobile
                        onClick={() => handleExpand(0)}
                        buttonDescription={
                          contentVisible
                            ? "Collapse calculator"
                            : "Get Started Expand calculator"
                        }
                        aria-expanded={contentVisible}
                        tabIndex={0}
                      >
                        {!contentVisible ? "Get Started" : "Close"}
                      </StyledButton>
                    )}
                </div>
              </div>
            </div>
            <Content className="w-full" style={expand}>
              <animated.div
                className="w-full flex flex-col mt-1 py-3 px-2"
                style={fade}
                active={contentVisible}
                ref={ref}
              >
                {contentVisible && (
                  <div className="lg:flex flex-col items-start px-3 md:px-8">
                    <RichText
                      tag="span"
                      className="block richtext text-3xl md:text-4xl font-trade-gothic-20"
                      field={{ value: heading }}
                    />
                    <RichText
                      tag="span"
                      className="block richtext font-graphie tracking-tighter mb-12 md:mb-8"
                      field={{ value: subheading }}
                    />
                    <span>* indicates a required field</span>
                    <div className="flex flex-col print:flex-row md:flex-row w-full">
                      <div className="mb-2 md:mb-0 md:w-1/3 md:pr-10 print:mr-4">
                        <div className="flex flex-col w-full">
                          <div className=" text-xs mt-2 mb-4">
                            {"Home Purchase Amount *"}
                          </div>
                          <FormatedInput
                            thousandSeparator={true}
                            decimalScale={2}
                            allowEmptyFormatting
                            prefix={"$  "}
                            value={amountValue}
                            onValueChange={(value) =>
                              setAmountValue(value.floatValue)
                            }
                            aria-label={"Home purchase amount"}
                            aria-required={true}
                            allowNegative={false}
                          />
                        </div>
                      </div>
                      <div className="mb-2 md:mb-0 md:w-1/3 md:pr-10 print:mr-4">
                        <div className="flex flex-col w-full">
                          <div className=" text-xs mt-2 mb-4">
                            {"Design Studio Amount"}
                          </div>
                          <FormatedInput
                            thousandSeparator={true}
                            decimalScale={2}
                            allowEmptyFormatting
                            prefix={"$  "}
                            value={designAmountValue}
                            onValueChange={(value) =>
                              setDesignAmountValue(value.floatValue)
                            }
                            aria-label={"Design Studio Amount"}
                            allowNegative={false}
                          />
                        </div>
                      </div>
                      <div className="mb-2 md:mb-0 md:w-1/3 md:pr-10 print:mr-4">
                        <div className="flex flex-col w-full">
                          <div className=" text-xs mt-2 mb-4">
                            {"Downpayment"}
                          </div>
                          <FormatedInput
                            thousandSeparator={true}
                            decimalScale={2}
                            allowEmptyFormatting
                            prefix={"$  "}
                            value={downpaymentValue}
                            onValueChange={(value) =>
                              setDownpaymentValue(value.floatValue)
                            }
                            aria-label={"Downpayment"}
                            allowNegative={false}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col print:flex-row md:flex-row w-full">
                      <div className="mb-2 md:mb-0 md:w-1/3 md:pr-10 print:mr-4">
                        <div className="flex flex-col w-full">
                          <div className=" text-xs mt-2 mb-4">
                            {"Interest Rate *"}
                          </div>
                          <div className="flex items-center">
                            <FormatedInput
                              value={interestRatevalue}
                              isNumericString={true}
                              onValueChange={(value) =>
                                setInterestRateValue(value.floatValue)
                              }
                              aria-label={"Percentage of Interest Rate"}
                              aria-required={true}
                              allowNegative={false}
                            />
                            <div className="-ml-8 z-10 mb-4 text-mattamy-blue font-graphie">
                              {"%"}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="mb-2 md:mb-0 md:w-1/3 md:pr-10">
                        <div className="flex flex-col w-full">
                          <div className=" text-xs mt-2 mb-4">
                            {"Loan Term *"}
                          </div>
                          <div className="flex items-center">
                            <FormatedInput
                              value={loanTermValue}
                              isNumericString={true}
                              onValueChange={(value) =>
                                setLoanTermValue(value.floatValue)
                              }
                              aria-label={"Loan Term on Years"}
                              aria-required={true}
                              allowNegative={false}
                            />
                            <div className="-ml-16 z-10 mb-4 text-mattamy-blue font-graphie">
                              {"Years"}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="mb-16 md:mb-12 mt-10 md:mt-16 print:hidden">
                      <StyledButton
                        className={cx({
                          //"focus:bg-white focus:text-mattamy-blue": isCalculatedButtonTouched,
                          "hover:bg-white hover:text-mattamy-blue":
                            !isMobile && textStyle === "white-blue",
                          "opacity-75": isNaN(result),
                        })}
                        color={
                          textStyle === "white-blue" ? "white" : "mattamy-blue"
                        }
                        variant={"outlined"}
                        textColor={
                          textStyle === "white-blue" ? "white" : "mattamy-blue"
                        }
                        fullWidthMobile
                        onClick={() => {
                          if (isMobile) {
                            setIsCalculatedButtonTouched(true);
                            setTimeout(() => {
                              setIsCalculatedButtonTouched(false);
                            }, 200);
                          }
                          setShowResult(true);
                        }}
                        wider
                        disabled={isNaN(result)}
                        noHoverMobile
                        buttonDescription={
                          "Click to calculate and display results"
                        }
                        tabIndex={0}
                      >
                        {"Calculate"}
                      </StyledButton>
                    </div>
                    {!isNaN(result) && showResult && (
                      <>
                        <ResultsTitle className="text-5xl tracking-tight leading-6 font-bold font-trade-gothic-20 my-4">
                          {resultsTitle}
                        </ResultsTitle>
                        <span className="text-sm tracking-widest font-graphie">
                          {resultsText}
                        </span>
                        <div className="w-full flex flex-col md:flex-row md:justify-between mt-2">
                          <span
                            className="text-6xl tracking-tight leading-6 font-bold font-trade-gothic-20 my-4"
                            role="status"
                          >
                            <NumberFormat
                              value={result}
                              className="break-all leading-none"
                              displayType={"text"}
                              thousandSeparator={true}
                              prefix={"$"}
                              decimalScale={2}
                            />
                          </span>
                          <div className="mt-12 md:mt-0 md:pr-10 print:hidden">
                            <StyledButton
                              className={cx({
                                "hover:bg-white hover:text-mattamy-blue":
                                  !isMobile && textStyle === "white-blue",
                              })}
                              color={
                                textStyle === "white-blue"
                                  ? "white"
                                  : "mattamy-blue"
                              }
                              variant={"outlined"}
                              textColor={
                                textStyle === "white-blue"
                                  ? "white"
                                  : "mattamy-blue"
                              }
                              fullWidthMobile
                              onClick={handlePrint}
                              printButton
                              buttonDescription={"print your results."}
                            >
                              {"Print your results"}
                            </StyledButton>
                          </div>
                        </div>
                      </>
                    )}
                    <div
                      className={cx(
                        "border-t mt-10 md:mt-12 mb-10 pt-4 md:pt-0",
                        {
                          "border-white": textStyle === "white-blue",
                          "border-mattamy-blue": textStyle === "blue-white",
                        }
                      )}
                    >
                      <RichText
                        tag="span"
                        className="richtext text-sm leading-6 font-graphie mt-5"
                        field={{ value: footerText }}
                      />
                    </div>
                    {isMobile && (
                      <>
                        <div className="my-8">
                          <StyledButton
                            className={cx("print:hidden", {
                              "hover:bg-mattamy-blue-dark":
                                textStyle === "white-blue",
                            })}
                            color={
                              textStyle === "white-blue"
                                ? "white"
                                : "mattamy-blue"
                            }
                            variant={"outlined"}
                            textColor={
                              textStyle === "white-blue"
                                ? "white"
                                : "mattamy-blue"
                            }
                            fullWidthMobile
                            onClick={() => toggleContent(!contentVisible)}
                            buttonDescription={"Collapse calculator"}
                          >
                            {"Close"}
                          </StyledButton>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </animated.div>
            </Content>
          </TitleCTAContainer>
        </Container>
      </section>
      <ABAModal
        isOpen={abaModalOpen}
        onClose={(e) => {
          e.preventDefault();
          setAbaModalOpen(false);
          setTimeout(() => {
            const current = acknowledgmentRef.current;
            if (current) {
              current.focus();
            }
          }, 100);
        }}
        title={mainTitle}
        text={mainText}
        subtitle={secondTitle}
        copy={secondText}
        ctaHref={ctas[ctaClickedIndex]}
        ctaText={abaModalCtaText}
        table={table}
        tableMobile={tableMobile}
      />
    </>
  );
};

export default MortgageCalculator;
